/**
 * @fileoverview Implements a lexical file indexing and search system using FlexSearch for fast full-text file searching. Provides functionality to build an index of files in a directory, search across file contents and paths, and rerank search results based on query relevance.
 */

import { Index } from 'flexsearch'
import { readFileSync } from 'fs'
import { FileData, getFileData, walkFolder } from '../folder'
import { info } from '@iterator/shared-node/util/logging'
import { FileIndex } from './indexing_types'

export async function doLexicalIndex(
  directory: string,
  testQuery: string | undefined = undefined,
  indexName: string = 'lexical',
): Promise<void> {
  info('Building lexical index', { directory, indexName, testQuery })
  const index = new LexicalIndex(indexName)
  await index.build(directory)

  info(`Index completed: ${indexName}`)

  if (testQuery) {
    info('')
    info(`Searching for "${testQuery}"...`)
    const results = await index.search(testQuery)
    info('Got results, reranking')
    const fileDatas = results.map((filePath) => getFileData(filePath, true))
    info(rerank(testQuery, fileDatas).map((f) => f.path))
  }
  // Exit to close the worker.
  process.exit(0)
}

export class LexicalIndex implements FileIndex {
  private index: Index
  readonly indexName: string
  private idToPath: Map<number, string> = new Map()

  constructor(indexName: string) {
    this.index = new Index({
      preset: 'memory',
    })
    this.indexName = indexName
  }

  async search(query: string): Promise<string[]> {
    const terms = query.split(' ')
    const searchRequests = terms.map((term) =>
      this.index.searchAsync(term, { suggest: true, limit: 1000 }),
    )
    const results = await Promise.all(searchRequests)
    const ids = new Set(results.flatMap((r) => r.map((id) => id as number)))
    return Array.from(ids).map((id) => this.idToPath.get(id)!)
  }

  async build(directory: string): Promise<FileIndex> {
    let count = 0
    walkFolder(directory, (path, type) => {
      if (type === 'file') {
        this.index.add(count, path + '\n\n' + readFileSync(path, 'utf8'))
        this.idToPath.set(count, path)
        count += 1
      }
    })
    return this
  }

  export(): Promise<string> {
    throw new Error('Method not implemented.')
  }

  import(_path: string): Promise<void> {
    throw new Error('Method not implemented.')
  }
}

type ScoredFile = { index: number; score: number; file: FileData }
/**
 * Reranks files based on (1) if the query is in file path and (2) how often the query appears in the file.
 * @param query search string, comprised of words separated by spaces
 * @param files list of files to rerank
 * @returns list of files sorted by relevance to the query
 */
export function rerank(query: string, files: FileData[]): FileData[] {
  let fileNameMatches: ScoredFile[] = []
  let nonFileNameMatches: ScoredFile[] = []

  const scoreFile = (query: string, file: FileData) => {
    const queryTerms = query.split(' ')

    let fileNameMatch = false
    for (const term of queryTerms) {
      if (file.path.toLowerCase().includes(term.toLowerCase())) {
        fileNameMatch = true
        break
      }
    }

    let numTextMatches = 0
    for (const term of queryTerms) {
      const regex = new RegExp(term, 'g')
      const matches = file.contents.match(regex)
      if (matches) {
        numTextMatches += matches.length
      }
    }
    return {
      isFileNameMatch: fileNameMatch,
      score: numTextMatches,
    }
  }

  for (let i = 0; i < files.length; i++) {
    const file = files[i]!
    const score = scoreFile(query, file)
    if (score.isFileNameMatch) {
      fileNameMatches.push({ index: i, score: score.score, file })
    } else {
      nonFileNameMatches.push({ index: i, score: score.score, file })
    }
  }

  const sort = (files: ScoredFile[]): FileData[] => {
    return files
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score
        }
        // Stable sort
        return a.index - b.index
      })
      .map((s) => s.file)
  }

  return sort(fileNameMatches).concat(sort(nonFileNameMatches))
}
